import { FC, useState } from 'react';
import { AuthenticatedWrapperProps, AuthTokenResponse } from './service.type';
import { EnvVariables, StorageKey } from '../utils/helper.type';
import AgreementModal from '../component/agreementModal/agreementModal';
import { useAcceptTermsData, useLogoutData } from '../hooks/useAuthData';
import { getEnvironmentVariable } from '../helper/config';
import { validateAndDecodeAuthToken } from '../utils/helper';
import { AcceptTermsRequest } from '@inplay/models';

/**
 *
 * @param props
 * @returns a terms and conditions component
 * @description This component is used as a wrapper for all authenticated aomponents except VerifyOTP.
 * This component returns a tnc popup if the user has not accept the tnc
 */
export const ConditionalTermsWrapper: FC<AuthenticatedWrapperProps> = (
  props,
) => {
  const [showTncModal, setShowTncModal] = useState<boolean>(() => {
    return localStorage.getItem(StorageKey.MUST_ACCEPT_TNC) !== 'false';
  });
  const [termsAccepted, setTermAccepted] = useState<boolean>(false);
  const [privacyAccepted, setPrivacyAccepted] = useState<boolean>(false);
  const [showPrivacyModal, setShowPrivacyModal] = useState<boolean>(false);
  const { acceptTerms, isAcceptLoading } = useAcceptTermsData();

  const { logout, logoutLoading } = useLogoutData();

  const handleTermsContinue = () => {
    setShowPrivacyModal(true);
  };

  const handlePrivacyContinue = async () => {
    try {
      console.log(`[ENTRY][handlePrivacyContinue]`);
      const version = getEnvironmentVariable(EnvVariables.REACT_APP_VERSION);
      const reqBody: AcceptTermsRequest = {
        version,
      };

      // fetch the staff pin from the local storage
      const staffPin: string | null = localStorage.getItem(
        StorageKey.STAFF_PIN,
      );
      // check if the user is either branch login or venue staff.
      if (staffPin) {
        reqBody.pin = staffPin;
      }
      await acceptTerms(reqBody);
      setShowPrivacyModal(false);
      setShowTncModal(false);
      console.log(
        `[handlePrivacyContinue][Closing tnc and privacy modals].......`,
      );
      console.log(`[EXIT][handlePrivacyContinue]`);
    } catch (error: unknown) {
      console.warn(`[handlePrivacyContinue]`, { error });
      throw error;
    }
  };

  const handlePrivacyBack = () => {
    setShowPrivacyModal(false);
  };

  const handleLogout = async () => {
    const decodedData: AuthTokenResponse | null = validateAndDecodeAuthToken();
    if (decodedData) {
      try {
        console.log(`[ENTRY][handleTermsBack]`);
        await logout(decodedData?.email);
        setShowTncModal(true);
      } catch (error: unknown) {
        console.log(`[handleTermsBack]`, { error });
        throw error;
      }
    }
  };

  if (showTncModal) {
    return (
      <>
        {
          <AgreementModal
            agreementTitle={'agreement.terms&condition'}
            agreementSubTitle={'agreement.terms&conditionSubTitle'}
            handleContinue={handleTermsContinue}
            handleOnClose={handleLogout}
            handleBack={handleLogout}
            content={'agreement.termsContent'}
            agreeAccepted={termsAccepted}
            setAgreeAccepted={setTermAccepted}
            agreementText={'agreement.agreementTermsText'}
            isBackLoading={logoutLoading}
            isCloseLoading={logoutLoading}
          />
        }
        {showPrivacyModal && (
          <AgreementModal
            agreementTitle={'agreement.privacyPolicy'}
            agreementSubTitle={'agreement.privacyPolicySubTitle'}
            handleContinue={handlePrivacyContinue}
            handleOnClose={handleLogout}
            handleBack={handlePrivacyBack}
            content={'agreement.termsContent'}
            agreeAccepted={privacyAccepted}
            setAgreeAccepted={setPrivacyAccepted}
            isLoading={isAcceptLoading}
            agreementText={'agreement.agreementPrivacyText'}
            isCloseLoading={logoutLoading}
          />
        )}
      </>
    );
  }

  return <>{props.children}</>;
};
